import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Dialog,
  Field,
  FileDrop,
  Input,
  Select,
  toast,
  useConfirm,
} from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DECOR_TYPES,
  useApi,
  type DecorDTO,
  type DecorType,
  type MimeImageType,
} from "../../api";
import { DECOR_STATUS_VARIANT } from "../../components/badges";
import { EnumBadge, objectUrl, Thumbnail } from "../../components/cells";
import { crudEditor, numberField, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/** `frame.offset` / `frame.front.resolution` read through nullable levels. */
const getX = (d: DecorDTO) => d.frame?.offset?.first ?? null;
const getY = (d: DecorDTO) => d.frame?.offset?.second ?? null;
const getW = (d: DecorDTO) => d.frame?.front?.resolution?.first ?? null;
const getH = (d: DecorDTO) => d.frame?.front?.resolution?.second ?? null;

function withOffset(d: DecorDTO, first: number | null, second: number | null): DecorDTO {
  const frame = { ...(d.frame ?? {}) };
  frame.offset = {
    first: first ?? d.frame?.offset?.first ?? 0,
    second: second ?? d.frame?.offset?.second ?? 0,
  };
  return { ...d, frame };
}

function withResolution(d: DecorDTO, first: number | null, second: number | null): DecorDTO {
  const frame = { ...(d.frame ?? {}) };
  frame.front = {
    ...(d.frame?.front ?? {}),
    resolution: {
      first: first ?? d.frame?.front?.resolution?.first ?? 0,
      second: second ?? d.frame?.front?.resolution?.second ?? 0,
    },
  };
  return { ...d, frame };
}

/**
 * Decors — the room scenes an artwork is composited into.
 *
 * Rows are per (product × decor) and carry a per-product `sort_order`, so
 * dragging is only meaningful once the product filter narrows the grid to one
 * product's siblings; that is when "Save order" lights up. Delete is a soft
 * archive: existing artwork references stay intact, the renderer skips it.
 */
export function DecorsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_DECOR);
  const title = t("navigation.item.decors", "Decors");

  const [order, setOrder] = useState<DecorDTO[] | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["decor"],
    queryFn: () => api.decors.findAll(),
    enabled: allowed,
  });

  const products = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const rows = data ?? [];
  const productList = products.data ?? [];
  const productById = useMemo(
    () => new Map(productList.map((entry) => [entry.id, entry])),
    [productList],
  );
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["decor"] });

  const cols = useMemo(() => {
    const c = defineColumns<DecorDTO>();
    const dash = (value: number | null) => (value === null ? "—" : String(value));
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => <Thumbnail url={objectUrl(row.thumbnail)} alt={row.code} />,
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 56 },
      ),
      c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
      c.text("product", (row) => productById.get(row.product_id)?.code ?? `#${row.product_id}`, {
        headerKey: "column.product",
        filter: "select",
        priority: 1,
      }),
      c.custom("x", (row) => getX(row), (row) => dash(getX(row)), {
        headerKey: "column.position-x",
        filter: "number",
        align: "right",
        priority: 4,
      }),
      c.custom("y", (row) => getY(row), (row) => dash(getY(row)), {
        headerKey: "column.position-y",
        filter: "number",
        align: "right",
        priority: 4,
      }),
      c.custom("w", (row) => getW(row), (row) => dash(getW(row)), {
        headerKey: "column.width",
        filter: "number",
        align: "right",
        priority: 4,
      }),
      c.custom("h", (row) => getH(row), (row) => dash(getH(row)), {
        headerKey: "column.height",
        filter: "number",
        align: "right",
        priority: 4,
      }),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={DECOR_STATUS_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 2 },
      ),
      c.number("sort_order", (row) => row.sort_order, {
        headerKey: "column.sort-order",
        priority: 3,
      }),
      c.text("tags", (row) => (row.tags ?? []).join(", "), {
        headerKey: "column.tags",
        priority: 5,
      }),
    ];
  }, [productById]);

  const editor = useMemo(
    () =>
      crudEditor<DecorDTO>({
        client: api.decors,
        refresh,
        t,
        // Create needs a background upload, so it goes through the dialog; the
        // grid never adds a row, and deleting is an archive action instead.
        canDelete: false,
        fields: {
          product: selectField(
            (row) => String(row.product_id),
            (row, value) => ({ ...row, product_id: Number(value) }),
            productList.map((entry) => ({
              value: String(entry.id),
              label: `${entry.code} (${entry.orientation} ${entry.ratio})`,
            })),
          ),
          x: numberField(
            (row) => getX(row),
            (row, value) => withOffset(row, value === "" ? 0 : Number(value), null),
          ),
          y: numberField(
            (row) => getY(row),
            (row, value) => withOffset(row, null, value === "" ? 0 : Number(value)),
          ),
          w: numberField(
            (row) => getW(row),
            (row, value) => withResolution(row, value === "" ? 0 : Number(value), null),
          ),
          h: numberField(
            (row) => getH(row),
            (row, value) => withResolution(row, null, value === "" ? 0 : Number(value)),
          ),
          tags: textField(
            (row) => (row.tags ?? []).join(", "),
            (row, value) => ({
              ...row,
              tags: value
                .split(",")
                .map((entry) => entry.trim())
                .filter(Boolean),
            }),
          ),
        },
        validate: (row) =>
          row.product_id
            ? null
            : { product: t("form.validation.field.required", "Value is required") },
      }),
    [api, productList, t],
  );

  const archive = async (row: DecorDTO) => {
    const ok = await confirm({
      title: t("dialog.decor-archive.title", "Archive decor"),
      message: t(
        "dialog.decor-archive.text",
        "The decor's status will be set to DELETED. Existing artwork references stay intact, but the renderer and admin grid will skip it.",
      ),
      confirmLabel: t("button.archive", "Archive"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.decors.archive(row.id);
      toast.success(t("notification.decor-archive.success", "Decor archived"));
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.decor-archive.error", "Failed to archive decor"),
      );
    }
  };

  /**
   * Reordering is only meaningful inside one product: `sort_order` is
   * per-product, so a drag across a mixed view would renumber unrelated rows.
   * Filtering the product column down to one is what makes it safe, and the
   * guard says so rather than silently corrupting the order.
   */
  const acceptOrder = (next: DecorDTO[]) => {
    const products = new Set(next.map((row) => row.product_id));
    if (products.size > 1) {
      toast.error(
        t(
          "notification.decor-reorder.filter-required",
          "Filter the Product column to one product before reordering.",
        ),
      );
      return;
    }
    setOrder(next);
  };

  const saveOrder = async () => {
    if (!order) return;
    try {
      // Renumber the dragged view, then persist each row; sort_order is
      // per-product so only the filtered set is touched.
      await Promise.all(
        order.map((row, index) => api.decors.update({ ...row, sort_order: index + 1 })),
      );
      toast.success(t("notification.order-save.success", "Order saved successfully"));
      setOrder(null);
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.order-save.error", "Failed to save order"),
      );
    }
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  // A pending drag replaces the rows it covers, so the grid shows the new order
  // before it is saved.
  const visible = order
    ? [...order, ...rows.filter((row) => !order.some((entry) => entry.id === row.id))]
    : rows;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<DecorDTO>
        data={visible}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        editor={editor}
        initialFilters={[{ id: "status", value: "ACTIVE" }]}
        onReorder={acceptOrder}
        actions={[
          {
            label: t("menu.item.decor.create", "Create decor"),
            icon: <Plus className="size-4" />,
            variant: "primary",
            write: true,
            onClick: () => setCreating(true),
          },
          {
            label: t("button.save", "Save"),
            icon: <Save className="size-4" />,
            variant: "success",
            write: true,
            disabled: order === null,
            onClick: () => void saveOrder(),
          },
        ]}
        rowActions={(row) =>
          row.status === "ACTIVE" ? (
            <Button
              size="sm"
              variant="ghost"
              title={t("button.archive", "Archive")}
              className="size-7 p-0 hover:text-risk"
              icon={<Trash2 className="size-3.5" />}
              onClick={() => void archive(row)}
            />
          ) : null
        }
      />

      <CreateDecorDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          void refresh();
        }}
      />
    </>
  );
}

/**
 * The simple create flow: a product, a background image and where the artwork
 * sits on it. Everything advanced (psd, perspective, foreground layers) is
 * filled in later by the renderer team, exactly as the Vaadin dialog scoped it.
 */
function CreateDecorDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [productId, setProductId] = useState("");
  const [posX, setPosX] = useState("0");
  const [posY, setPosY] = useState("0");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [type, setType] = useState<DecorType>("DECOR");
  const [mime, setMime] = useState<MimeImageType>("JPG");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const products = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // A CUSTOM-ratio product has no fixed aspect to composite into.
  const options = (products.data ?? [])
    .filter((entry) => entry.ratio !== "CUSTOM")
    .map((entry) => ({
      value: String(entry.id),
      label: `${entry.code} (${entry.orientation} ${entry.ratio})`,
    }));

  const complete = productId && width && height && posX !== "" && posY !== "" && file;

  const submit = async () => {
    if (!complete || !file) {
      toast.error(
        t("notification.form-incomplete", "Please fill in every field and upload an image"),
      );
      return;
    }
    setBusy(true);
    try {
      await api.decors.createSimple({
        product_id: Number(productId),
        pos_x: Number(posX),
        pos_y: Number(posY),
        width: Number(width),
        height: Number(height),
        type,
        mime,
        background: file,
      });
      toast.success(t("notification.decor-create.success", "Decor created"));
      onCreated();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.decor-create.error", "Failed to create decor"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.decor-create.title", "Create decor")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button variant="success" loading={busy} onClick={() => void submit()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("form.field.product", "Product")} required>
          <Select
            value={productId}
            options={options}
            placeholder={t("form.field.select", "Select")}
            onChange={(event) => setProductId(event.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("form.field.position-x", "Position X")} required>
            <Input type="number" min={0} value={posX} onChange={(e) => setPosX(e.target.value)} />
          </Field>
          <Field label={t("form.field.position-y", "Position Y")} required>
            <Input type="number" min={0} value={posY} onChange={(e) => setPosY(e.target.value)} />
          </Field>
          <Field label={t("form.field.width", "Width")} required>
            <Input type="number" min={1} value={width} onChange={(e) => setWidth(e.target.value)} />
          </Field>
          <Field label={t("form.field.height", "Height")} required>
            <Input
              type="number"
              min={1}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
            />
          </Field>
          <Field label={t("form.field.type", "Type")}>
            <Select
              value={type}
              options={DECOR_TYPES.map((entry) => ({ value: entry, label: entry }))}
              onChange={(event) => setType(event.target.value as DecorType)}
            />
          </Field>
          <Field label={t("form.field.mime", "Image format")}>
            <Select
              value={mime}
              options={[
                { value: "JPG", label: "JPG" },
                { value: "PNG", label: "PNG" },
              ]}
              onChange={(event) => setMime(event.target.value as MimeImageType)}
            />
          </Field>
        </div>
        <Field label={t("form.field.upload", "Upload")} required>
          <FileDrop
            accept="image/jpeg,image/png"
            files={file ? [file] : []}
            onRemove={() => setFile(null)}
            onFiles={(files) => {
              const picked = files[0];
              if (!picked) return;
              setFile(picked);
              // Mirror the upload's own content type into the mime picker so
              // the server stores the format it actually received.
              if (picked.type.includes("png")) setMime("PNG");
              else if (picked.type.includes("jpeg") || picked.type.includes("jpg")) setMime("JPG");
            }}
          />
        </Field>
        {!complete && (
          <Badge variant="warning" size="sm">
            {t("notification.form-incomplete", "Please fill in every field and upload an image")}
          </Badge>
        )}
      </div>
    </Dialog>
  );
}
